import { type DeleteFocusAreaInstanceMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteFocusAreaInstanceMutationDocument } from "./focus-area-instance.gql";
import { EVENT_FOCUS_AREA_INSTANCE_DELETED, trackFocusAreaEvent } from "./tracking";

export type DeletedFocusAreaInstance = DeleteFocusAreaInstanceMutation["deleteFocusAreaInstance"];

export interface UseDeleteFocusAreaInstanceProps {
  onCompleted?: (data: DeletedFocusAreaInstance) => void;
  onError?: (e: Error) => void;
}

export const useDeleteFocusAreaInstance = ({ onCompleted, onError }: UseDeleteFocusAreaInstanceProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: { id: string }) => {
      const client = createOperationClient("deleteFocusAreaInstance");
      const data = await client.request(deleteFocusAreaInstanceMutationDocument, { input });
      return data.deleteFocusAreaInstance;
    },
    onSuccess: (focusAreaInstance) => {
      // Remove the focus area instance from the cache
      queryClient.removeQueries({
        queryKey: queryKeys.focusAreas.instance(focusAreaInstance.id),
      });

      // Remove from owner-specific cache
      queryClient.removeQueries({
        queryKey: queryKeys.focusAreas.instanceByOwner(focusAreaInstance.ownerId),
      });

      // Invalidate the owner's detail query to reflect the change
      if (focusAreaInstance.ownerType === "CLIMATE_ACTION") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.detail(focusAreaInstance.ownerId),
        });
      } else if (focusAreaInstance.ownerType === "PROJECT") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(focusAreaInstance.ownerId),
        });
      } else if (focusAreaInstance.ownerType === "INDICATOR") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.indicators.detail(focusAreaInstance.ownerId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.indicators.all,
        });
      }

      // Invalidate and refetch focus area lists for this workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.focusAreas.list(focusAreaInstance.workspaceId),
      });

      // Invalidate climate action list for this workspace since they include focus area instance data
      queryClient.invalidateQueries({
        queryKey: queryKeys.climateActions.list(focusAreaInstance.workspaceId),
      });

      // Invalidate project queries since they include focus area instance data
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
      });

      // Invalidate workspace queries since they include focus area data
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.all,
      });

      // Track the event
      trackFocusAreaEvent(EVENT_FOCUS_AREA_INSTANCE_DELETED, focusAreaInstance);

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(focusAreaInstance);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteFocusAreaInstance: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
